import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MaskedLines } from "@/components/site/Reveal";
import AnimatedBackground from "@/components/site/AnimatedBackground";
import { useContent } from "@/context/ContentContext";
import { ArrowUpRight, Check } from "lucide-react";

const TRACKS = ["All", "SAS®", "R", "SQL", "Statistics"];

export default function Courses() {
  const { content } = useContent();
  const COURSES = content.courses || [];
  const [track, setTrack] = useState("All");
  const filtered = track === "All" ? COURSES : COURSES.filter((c) => c.track === track);

  return (
    <div data-testid="courses-page" className="relative pt-32">
      <AnimatedBackground variant="cool" />
      <section className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <p className="font-mono text-sm uppercase tracking-[0.3em] text-brand-blue">The curriculum</p>
        <h1 className="mt-6 font-display text-5xl font-medium leading-[0.95] tracking-tight text-white sm:text-7xl">
          <MaskedLines lines={["Courses built"]} />
          <MaskedLines lines={["for the real job."]} delay={0.2} lineClassName="text-brand-gradient" />
        </h1>

        <div className="mt-12 flex flex-wrap gap-3" data-testid="course-filters">
          {TRACKS.map((t) => (
            <button
              key={t}
              data-testid={`filter-${t.toLowerCase().replace(/[^a-z0-9]/g, '')}`}
              onClick={() => setTrack(t)}
              className={`border px-5 py-2 font-mono text-sm uppercase tracking-[0.2em] transition-colors duration-300 ${
                track === t
                  ? "border-brand-blue bg-brand-blue text-white"
                  : "border-white/15 text-slate-400 hover:border-white/40 hover:text-white"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 py-16 lg:px-12">
        <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-2">
          <AnimatePresence mode="popLayout">
            {filtered.map((c, i) => (
              <motion.div
                key={c.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, delay: (i % 2) * 0.05 }}
                data-testid={`course-card-${c.id}`}
                className="group relative flex flex-col gap-8 overflow-hidden bg-navy p-10 transition-colors duration-500 hover:bg-surface-2"
              >
                <span className="absolute inset-x-0 top-0 h-0.5 origin-left scale-x-0 bg-gradient-to-r from-brand-blue to-brand-magenta transition-transform duration-500 group-hover:scale-x-100" />
                <div className="flex items-center justify-between">
                  <span className="border border-brand-blue/40 bg-brand-blue/10 px-2.5 py-1 font-mono text-sm uppercase tracking-[0.2em] text-brand-blue">{c.code}</span>
                  <span className="font-mono text-sm uppercase tracking-[0.15em] text-brand-magenta">{c.track} track</span>
                </div>

                <div>
                  <h3 className="font-display text-3xl font-medium text-white">{c.title}</h3>
                  <p className="mt-3 text-lg font-light leading-relaxed text-slate-400">{c.blurb}</p>
                </div>

                <ul className="grid gap-2.5">
                  {c.topics.map((t) => (
                    <li key={t} className="flex items-center gap-2.5 text-base text-slate-300">
                      <Check size={16} className="shrink-0 text-brand-magenta" /> {t}
                    </li>
                  ))}
                </ul>

                <div className="mt-auto flex items-end justify-between border-t border-white/10 pt-6">
                  <div className="flex flex-col gap-1 font-mono text-base uppercase tracking-[0.15em] text-slate-400">
                    <span>{c.level}</span>
                    <span>{c.duration} · {c.format}</span>
                  </div>
                  <Link
                    to={`/contact?course=${encodeURIComponent(c.title)}`}
                    data-testid={`course-enquire-${c.id}`}
                    className="group/btn flex items-center gap-2 bg-brand-blue px-5 py-2.5 font-mono text-sm uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-brand-magenta"
                  >
                    Enquire
                    <ArrowUpRight size={14} className="transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </section>
    </div>
  );
}
